import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Stack,
  Group,
  Button,
  Badge,
  List,
  Alert,
  Loader,
  Modal,
  Divider
} from '@mantine/core';
import {
  IconCheck,
  IconCrown,
  IconStar,
  IconExclamationCircle,
  IconCreditCard
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { api } from '../services/api';

const SubscriptionPage = () => {
  const { isAuthenticated } = useSelector(state => state.auth);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptionData();
    }
  }, [isAuthenticated]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      // Fetch current subscription
      const subResponse = await api.get('/subscription/');
      if (subResponse.plan) {
        setSubscription(subResponse);
      }
      
      // Fetch available plans
      const plansResponse = await api.get('/subscription/plans/');
      setPlans(plansResponse.plans);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to load subscription information',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    try {
      setSubscribing(true);
      const response = await api.post('/subscription/', { plan });
      setSubscription(response);
      notifications.show({
        title: 'Success!',
        message: `You've successfully subscribed to ${response.plan_name}!`,
        color: 'green'
      });
    } catch (err) {
      console.error('Error subscribing:', err);
      notifications.show({
        title: 'Error',
        message: err.response?.data?.details?.non_field_errors?.[0] || 'Failed to create subscription',
        color: 'red'
      });
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await api.delete('/subscription/');
      setSubscription(null);
      setCancelModalOpen(false);
      notifications.show({
        title: 'Subscription Canceled',
        message: 'Your subscription has been canceled',
        color: 'yellow'
      });
      fetchSubscriptionData();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      notifications.show({
        title: 'Error',
        message: 'Failed to cancel subscription',
        color: 'red'
      });
    }
  };

  // Authentication check handled by ProtectedRoute wrapper

  if (loading) {
    return (
      <ProtectedRoute requireAuth={true} redirectTo="/">
        <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
      </ProtectedRoute>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <ProtectedRoute requireAuth={true} redirectTo="/">
      <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Subscription Plans</Title>
          <Text c="dimmed" mt="xs">
            Choose the plan that's right for you
          </Text>
        </div>

        {/* Current Subscription Status */}
        {subscription && subscription.is_active && (
          <Alert
            icon={<IconCrown size="1rem" />}
            color="green"
            variant="light"
            title="Active Subscription"
          >
            <Text size="sm">
              You are currently subscribed to <strong>{subscription.plan_name}</strong> 
              {' '}(${subscription.price}/month)
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              Current period ends: {formatDate(subscription.current_period_end)}
            </Text>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              mt="sm"
              onClick={() => setCancelModalOpen(true)}
            >
              Cancel Subscription
            </Button>
          </Alert>
        )}

        {/* Subscription Plans */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {plans && Object.entries(plans).map(([key, plan]) => {
            const isCurrentPlan = subscription?.plan === key;
            const isActive = subscription?.is_active && isCurrentPlan;
            
            return (
              <Card
                key={key}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                style={{
                  borderColor: isActive ? 'var(--mantine-color-green-6)' : undefined,
                  borderWidth: isActive ? 2 : 1
                }}
              >
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Group gap="xs">
                        <Title order={3}>{plan.name}</Title>
                        {key === 'paypiggiepro' && (
                          <Badge color="gradient" variant="filled" size="sm">
                            PRO
                          </Badge>
                        )}
                      </Group>
                      <Title order={2} mt="xs">
                        ${plan.price}
                        <Text span size="sm" c="dimmed" fw={400}>
                          /month
                        </Text>
                      </Title>
                    </div>
                    {key === 'paypiggiepro' && (
                      <IconStar size="1.5rem" color="var(--mantine-color-yellow-6)" />
                    )}
                  </Group>

                  <Divider />

                  <List
                    spacing="sm"
                    size="sm"
                    icon={
                      <IconCheck
                        size="1rem"
                        color="var(--mantine-color-green-6)"
                      />
                    }
                  >
                    {plan.features.map((feature, index) => (
                      <List.Item key={index}>{feature}</List.Item>
                    ))}
                  </List>

                  {isActive ? (
                    <Badge color="green" variant="filled" size="lg" fullWidth>
                      Current Plan
                    </Badge>
                  ) : (
                    <Button
                      fullWidth
                      variant={key === 'paypiggiepro' ? 'filled' : 'light'}
                      leftSection={<IconCreditCard size="1rem" />}
                      onClick={() => handleSubscribe(key)}
                      disabled={subscribing || (subscription && subscription.is_active)}
                      loading={subscribing}
                    >
                      {subscription && subscription.is_active
                        ? 'Already Subscribed'
                        : `Subscribe to ${plan.name}`}
                    </Button>
                  )}
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>

        {/* Benefits Section */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={3}>Subscription Benefits</Title>
            <Text size="sm" c="dimmed">
              All subscriptions include:
            </Text>
            <List size="sm" spacing="xs">
              <List.Item>Instant activation</List.Item>
              <List.Item>Cancel anytime</List.Item>
              <List.Item>Secure payment processing</List.Item>
              <List.Item>24/7 customer support</List.Item>
            </List>
          </Stack>
        </Card>
      </Stack>

      {/* Cancel Subscription Modal */}
      <Modal
        opened={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel Subscription"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconExclamationCircle size="1rem" />} color="yellow">
            <Text size="sm">
              Are you sure you want to cancel your subscription? You will lose access to all premium features at the end of your current billing period.
            </Text>
          </Alert>
          <Text size="sm" c="dimmed">
            Your subscription will remain active until: {subscription && formatDate(subscription.current_period_end)}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setCancelModalOpen(false)}
            >
              Keep Subscription
            </Button>
            <Button
              color="red"
              onClick={handleCancelSubscription}
            >
              Cancel Subscription
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
    </ProtectedRoute>
  );
};

export default SubscriptionPage;